/**
 * MelodyStrategies.js
 * Advanced procedural generation for melodic lines based on genre and contour.
 */

import { SCALE_INTERVALS, NOTES, normalizeKey } from '../data/theoryConstants.js';
import { MelodicSyntax } from './OrchestralLogic/MelodicSyntax.js';
import { getBeatStrength, generateEuclideanRhythm, patternToEvents, getProceduralPhraseType, getPhrasePattern } from './RhythmLogic.js';
import { MELODY_WEIGHTS, MELODIC_PROFILE_PRESETS } from '../data/StrategyConfig.js';
import { checkClash } from './RegistryUtils.js';
import { getGlobalConstraints } from './SeededConfig.js';

// No Magic Numbers: Phrasing Constants
const TEMPO_PHRASING_THRESHOLDS = {
    SLOW: 80,
    FAST: 130,
    SLOW_MODIFIER: 1.2,
    FAST_MODIFIER: 0.8
};

// Helper to calculate phrase structure based on length (Task 2)
const calculatePhraseStructure = (length) => {
    if (length <= 4) return 'compressed'; // Short
    if (length <= 12) return 'standard';  // Medium (8-12)
    return 'extended'; // Long (16+)
};

// --- UTILITIES ---

/**
 * Task 3: Motif Glue - Similarity Calculator
 * Compares two rhythm patterns and returns a score (0.0 - 1.0).
 */
export const calculateSimilarity = (patternA, patternB) => {
    if (!patternA || !patternB) return 0;
    if (patternA.length === 0 && patternB.length === 0) return 1.0;
    if (patternA.length === 0 || patternB.length === 0) return 0;

    // 1. Count Match
    const countDiff = Math.abs(patternA.length - patternB.length);
    const countScore = Math.max(0, 1.0 - (countDiff / Math.max(patternA.length, patternB.length)));

    // 2. Position Match (Grid check)
    let hits = 0;
    patternA.forEach(a => {
        // Check if B has a note at roughly the same time
        const hasMatch = patternB.some(b => Math.abs(b.s - a.s) < 0.05);
        if (hasMatch) hits++;
    });
    const posScore = hits / patternA.length;

    // Weighted Average
    return (countScore * 0.4) + (posScore * 0.6);
};

export const augmentRhythm = (pattern, ratio = 2) => {
    if(!pattern) return [];
    return pattern.map(p => ({
        ...p,
        s: p.s * ratio,
        l: p.l * ratio
    }));
};

export const transformPattern = (pattern, type) => {
    if (!pattern || pattern.length === 0) return pattern;
    const newPattern = pattern.map(p => ({...p}));
    if (type === 'reverse') newPattern.reverse();
    else if (type === 'densify') {
        const dense = [];
        newPattern.forEach(p => {
            if (p.l >= 0.5) {
                dense.push({ ...p, l: p.l / 2 });
                dense.push({ ...p, s: p.s + (p.l / 2), l: p.l / 2 });
            } else {
                dense.push(p);
            }
        });
        return dense;
    }
    else if (type === 'inversion') {
        newPattern.forEach(p => {
            if (p.i !== undefined) p.i = -p.i;
        });
    }
    else if (type === 'retrograde') newPattern.reverse();
    return newPattern;
};

/**
 * Helper: Consolidate adjacent notes based on context.
 * Used to reduce "busy-ness" by merging notes into longer phrasing.
 * @param {Array} pattern - Rhythmic pattern {s, l}
 * @param {Object} context - { drumDensity, bassDensity, random }
 */
const consolidateElements = (pattern, context) => {
    if (!pattern || pattern.length < 2) return pattern;
    const result = [];
    let current = { ...pattern[0] };

    for (let i = 1; i < pattern.length; i++) {
        const next = pattern[i];

        // Check adjacency (within small tolerance)
        const endOfCurrent = current.s + current.l;
        const gap = next.s - endOfCurrent;

        let shouldMerge = false;

        // Condition 1: High Drum Density -> Force Space/Contrast
        const drumDensity = context.drumDensity || 0;
        if (drumDensity > 0.7 && gap < 0.1) {
             // 40% chance to merge to create "half-time" feel against busy drums
             if ((context.random ? context.random.next() : Math.random()) < 0.4) shouldMerge = true;
        }

        // Condition 2: Bass Anchor
        // If Bass is playing long notes (low density), blend melody to match?
        // Actually prompt says: "If BassDuration is Long (1n), increase bias to merge"
        const bassLong = context.bassDuration && context.bassDuration >= 4.0; // 1n
        if (bassLong && gap < 0.1) {
             if ((context.random ? context.random.next() : Math.random()) < 0.5) shouldMerge = true;
        }

        if (shouldMerge) {
            // Merge: Extend current length
            // If there is a gap, it becomes part of the note?
            // Usually we merge adjacent. If there's a gap (rest), we might skip merging unless we want legato over rest.
            // Let's assume we only merge if gap is negligible.
            current.l += (next.l + gap);
        } else {
            result.push(current);
            current = { ...next };
        }
    }
    result.push(current);
    return result;
};

/**
 * Helper: Apply variance to a value.
 */
const fuzz = (value, variance, random) => {
    if (!random) return value;
    const range = variance * 2;
    return value + ((random.next() * range) - variance);
};

/**
 * Helper: Linear Interpolation
 */
const lerp = (start, end, t) => {
    return start * (1 - t) + end * t;
};

/**
 * Weighted Random Choice
 * @param {Array} options - [{ value, weight }]
 * @param {Object} random - Random generator
 */
const weightedRandom = (options, random) => {
    let total = options.reduce((acc, opt) => acc + opt.weight, 0);
    let r = (random ? random.next() : Math.random()) * total;
    for (let opt of options) {
        if (r < opt.weight) return opt.value;
        r -= opt.weight;
    }
    return options[0].value;
};

/**
 * Get Scale Degrees from Root and Type
 */
const getScaleNotes = (root, type) => {
    const intervals = SCALE_INTERVALS[type] || SCALE_INTERVALS.major;
    const normalizedRoot = normalizeKey(root);
    const rootIdx = NOTES.indexOf(normalizedRoot);
    return intervals.map(i => (rootIdx + i) % 12);
};

/**
 * Helper: Determine Modal scale based on chord function relative to key
 */
const getModalScaleType = (chord, keyContext) => {
    if (!keyContext || !keyContext.rootKey || !chord) return 'major';

    // Calculate interval between key root and chord root
    const keyRootIdx = NOTES.indexOf(normalizeKey(keyContext.rootKey));
    const chordRootIdx = chord.rootPitchClass % 12;
    let interval = chordRootIdx - keyRootIdx;
    if (interval < 0) interval += 12;

    const isMinorKey = keyContext.scaleType && keyContext.scaleType.includes('minor');

    if (isMinorKey) {
        if (interval === 0) return 'minor'; // i
        if (interval === 3) return 'major'; // III
        if (interval === 7) return 'minor'; // v
        return 'minor';
    } else {
        if (interval === 2) return 'dorian';
        if (interval === 4) return 'phrygian';
        if (interval === 5) return 'lydian';
        if (interval === 7) return 'mixolydian';
        if (interval === 9) return 'minor';
        if (interval === 11) return 'locrian';
        return 'major';
    }
};

/**
 * Melodic Walker
 * Generates a pitch based on previous pitch, current chord, and constraints.
 */
const getNextPitch = (prevPitch, chord, scaleType, context) => {
    // --- REFACTORED: Weights from Config ---
    const melodyConfig = (context.config && context.config.melody) ? context.config.melody : MELODY_WEIGHTS;

    // Task 1: Safe Zone Governor
    const constraints = getGlobalConstraints(context);
    const melodicRules = context.traits?.melodicRules || {};

    let intervalProfile = context.intervalProfile || melodyConfig.intervalProfiles.default;

    // Phase 6 Narrative Tension: High tension -> More Leaps
    const tension = context.getTension ? context.getTension(context.barIndex || 0) : 0.5;

    // Dynamic Interval Profile (LERP based on tension AND Melodic Range)
    // Extract Melodic Range (Virtuosity) from Config
    const melodicRange = (context.config && context.config.vectors) ? context.config.vectors.melodicRange : 0.5;

    // Influence 1: Tension (Existing Logic)
    let tensionT = 0;
    if (tension > 0.2) {
        // Non-linear Tension Mapping
        // Stay "safe" (low T) until tension is very high (> 0.7)
        // Using power curve p^3
        const rawT = (tension - 0.2) / 0.8;
        tensionT = Math.pow(Math.max(0, Math.min(1, rawT)), 3);
    }

    // Influence 2: Melodic Range (Config)
    // Map linear input to exponential influence
    const rangeT = Math.pow(melodicRange, 1.5);

    // Combine influence
    const t = Math.max(tensionT, rangeT);

    // Apply interpolation using non-linear factor
    // If Melodic Range is high (t -> 1), we want MORE Leaps (Wider intervals).
    // If Tension is high, we usually want steps (Runs), but 't' combines both.
    // Let's split them. Range drives Leaps. Tension drives Steps.

    // Range Influence
    intervalProfile = {
        step: lerp(intervalProfile.step, Math.max(0.2, intervalProfile.step - 0.5), rangeT), // Aggressively Reduce steps if range high
        skip: lerp(intervalProfile.skip, Math.min(0.6, intervalProfile.skip + 0.1), rangeT),
        leap: lerp(intervalProfile.leap, Math.min(0.8, intervalProfile.leap + 0.5), rangeT)  // Aggressively Boost leaps
    };

    // Fix: Reduce Step Bonus (Entropy) if Range is High
    if (rangeT > 0.7) {
        stepBonus = 10; // Neutralize step bias
        leapBonus = 50; // Encourge exploration
    }

    // Task 1: Goal-Oriented Gravity
    const goalNote = context.goalNote;
    const progress = context.progress || 0; // 0.0 to 1.0 within phrase

    // Range Expansion based on Melodic Range
    // The Anti-Clamp: Use a wide default range (C2-C7) to prevent abstract flatlining.
    // Hardware constraints are handled downstream in RoleAllocator.
    let { min, max } = context.range || { min: 36, max: 96 };

    // Expand or contract range
    // 0.0 -> narrowed by 5 semitones each side
    // 1.0 -> expanded by 12 semitones each side
    const rangeMod = (melodicRange - 0.5) * 24; // -12 to +12
    min = Math.max(0, min - rangeMod);
    max = Math.min(127, max + rangeMod);

    const random = context.random; // Passed from MusicEngine
    const strictMode = context.strictMode || false;
    // Defect Fix: Use harmonicComplexity vector (0-1) mapped to 0-100
    // Operation Jailbreak: Use this freedom scalar to unlock intervals
    const complexity = (context.config && context.config.vectors) ? (context.config.vectors.harmonicComplexity * 100) : 50;

    // Fix: "Fake Freedom" - Read vector directly, do not rely on constraints which lacks this property.
    // If vector exists, use it. Else fallback to 0.5.
    const harmonicFreedom = (context.config && context.config.vectors && context.config.vectors.harmonicComplexity !== undefined)
                            ? context.config.vectors.harmonicComplexity
                            : 0.5;

    // Adjust Weights based on Complexity
    let repeatWeight = melodyConfig.weights.repeat;
    let leapWeightMult = 1.0;

    // Strict Mode Override (Baroque)
    if (strictMode) {
        repeatWeight = 0; // Baroque hates repeated notes (motor rhythm)
    }
    else if (complexity > melodyConfig.complexity.highThreshold) {
        repeatWeight = Math.max(0, repeatWeight - melodyConfig.complexity.repeatPenalty);
        leapWeightMult = melodyConfig.complexity.leapBoost;
    } else if (complexity < melodyConfig.complexity.lowThreshold) {
        repeatWeight += melodyConfig.complexity.repeatBoost;
    }

    let effectiveScaleType = scaleType;

    const useModal = context.traits && (context.traits.modalHarmony || context.traits.name === 'Jazz' || context.traits.name === 'Fusion');

    if (useModal) {
        effectiveScaleType = getModalScaleType(chord, { rootKey: context.rootKey, scaleType: context.scaleType });
    }

    if (context.isImpressionist) {
        if (context.impressionistScale) {
            effectiveScaleType = context.impressionistScale;
        }
    }

    const scaleRoot = useModal ? NOTES[chord.rootPitchClass % 12] : (context.rootKey || 'C');

    const scalePCs = new Set(getScaleNotes(scaleRoot, effectiveScaleType));
    const chordNotesSafe = (chord && Array.isArray(chord.notes)) ? chord.notes : [];
    const chordPCs = new Set(chordNotesSafe.map(n => n % 12));

    const center = prevPitch || 72;
    const candidates = [];

    const beatStrength = (context.beatPos !== undefined && context.timeSig)
        ? getBeatStrength(context.beatPos, context.timeSig)
        : 0.5;

    const isStrongBeat = beatStrength >= 0.8;

    const isLastNote = context.isLastNote;
    const phraseType = context.phraseType;

    const harmonicProfile = context.harmonicProfile || { strongBeatChordToneWeight: 0.8, weakBeatExtensionWeight: 0.4 };

    let guideTones = [];
    if (chord.notes && chord.notes.length >= 2) {
        guideTones = chord.notes.map(n => n % 12);
    }

    // Voice Registry Check Time
    const currentAbsoluteTime = (context.barStart || 0) + (context.beatPos || 0);

    for (let i = center - 12; i <= center + 12; i++) {
        if (i < min || i > max) continue;

        const pc = i % 12;

        const isChordTone = chordPCs.has(pc);
        const isScaleTone = scalePCs.has(pc);

        if (isStrongBeat) {
             if (!isChordTone) {
                 if (strictMode) continue;
             }
        }

        if (!scalePCs.has(pc)) {
             const isChordTone = chordPCs.has(pc);
             const isChromaticStyle = context.scaleType === 'chromatic' || context.scaleType === 'whole_tone';

             if (strictMode) {
                 if (!isChromaticStyle && !isChordTone) continue;
             } else {
                 if (!isChromaticStyle && !isChordTone) continue;
             }
        }

        const isAppoggiatura = melodicRules && melodicRules.allowAppoggiatura && isStrongBeat && chordPCs.has((pc - 1 + 12) % 12);

        if (melodicRules && melodicRules.allowNonChordTones && Math.random() > 0.5) {
            // Allow non-chord tones (Impressionist wobble)
        }

        if (isLastNote) {
            const rootPC = getScaleNotes(context.rootKey || 'C', effectiveScaleType)[0];
            const interval = (pc - rootPC + 12) % 12;

            if (phraseType === 'question') {
                const isUnstable = [2, 5, 7, 11].includes(interval);
                if (!isUnstable) continue;
            }
            if (phraseType === 'answer') {
                const isStable = [0, 3, 4, 7].includes(interval);
                if (!isStable) continue;
            }
        }

        // --- 70% SHIFT: Context Weighted Decision ---
        // Base Probability: 0.2 (20 points)
        let weight = 20;

        // Task 2: Dynamic Gravity (Scale Weights by Conformity)
        const conformity = 1.0 - harmonicFreedom; // 0.0 (Free) to 1.0 (Strict)

        // Directive 1: Metric Gravity (Elastic Anchor)
        // Decouple "Safe Note" probability from global tension, tie to Metric Grid.
        // Strong Beats (1 & 3): High Gravity (Anchor).
        // Weak Beats (2 & 4): Medium Gravity.
        // Off-Beats: Zero/Low Gravity (Free).

        let metricGravity = 0;
        if (beatStrength >= 0.8) metricGravity = 1.0; // Strong (1, 3)
        else if (beatStrength >= 0.4) metricGravity = 0.5; // Weak (2, 4)
        else metricGravity = 0.0; // Off-beat

        // --- DEEP ARCHITECTURE: Entropy Balancing (Moved Up) ---
        let stepBonus = 50;
        let leapBonus = 5;

        // Override defaults with config rules if present
        if (melodicRules.stepBonus !== undefined) stepBonus = melodicRules.stepBonus;

        // Context Injection Check (assuming context.entropyState exists, populated by wrapper)
        if (context.entropyState) {
            const currentRatio = context.entropyState.stepRatio || 1.0;
            let target = 0.65; // Ideal Step Ratio for Lyrical/Epic

            // Fix: Adjust target for Strict Mode (Baroque)
            if (strictMode) target = 0.85;

            if (currentRatio > target + 0.1) {
                // Too many steps -> Boost Leaps
                stepBonus = 20;
                leapBonus = 25;
            } else if (currentRatio < target - 0.2) {
                // CRITICAL DEFICIT (Ratio < 0.45)
                // Panic Mode: Force Steps
                stepBonus = Math.max(stepBonus, 150);
                leapBonus = -50; // Discourage, don't ban (allows 10-20% leaps)
            } else if (currentRatio < target - 0.1) {
                // Too many leaps -> Boost Steps
                stepBonus = Math.max(stepBonus, 100);
                leapBonus = 10; // Positive but small (Allow leaps to prevent stuckness)
            }
        }

        // Context 1: Harmonic Change (Is Chord Tone?)
        // Scaling Bonus: +10 (Free) to +50 (Strict) -> Modulated by Metric Gravity
        // If High Gravity -> High Bonus. If Low Gravity -> Low Bonus.
        let chordToneBonus = 10 + (40 * conformity) + (30 * metricGravity);

        // Fix: Distance-Aware Chord Bonus (Favor Stepwise Chord Tones)
        // If the chord tone is a leap away (> 4 semitones), reduce its attraction
        // to prevent "Jumping" just to hit a harmony.
        const dist = Math.abs(i - center);

        // Fix: Lyrical Mode (Epic) - Reduce Chord Tone Attraction to favor Steps
        if (intervalProfile.step > 0.7) {
            chordToneBonus *= 0.2; // Reduce pull of arpeggios
            stepBonus = Math.max(stepBonus, 200); // Boost steps (Force Scale Runs)
        }

        // Data-Driven Step Force (Impressionist/Baroque)
        if (melodicRules.forceSteps) {
             chordToneBonus *= 0.1; // Reduce pull
             // stepBonus is already set by melodicRules in upper scope if passed
        }

        // PANIC MODE LOGIC: Crush ALL leaps (> 2 semitones)
        if (stepBonus >= 200 && dist > 2) {
             chordToneBonus *= 0.05; // Virtual Veto on Arpeggiation
        }
        // Strict Mode (Baroque): Aggressively Favor Scalar Motion
        else if (strictMode) {
             if (dist <= 2 && isScaleTone) {
                 weight += 200; // Overwhelming bonus
                 if (dist === 0) weight -= 50;
             } else {
                 weight -= 500; // Crush everything else
             }
        }
        else if (dist > 4) {
            chordToneBonus *= 0.4;
        }

        if (isChordTone) {
            weight += chordToneBonus;
        } else {
            // Non-Chord Tone Logic

            // 1. Scalar Penalty (Soft Veto)
            // Apply penalty proportional to conformity AND Metric Gravity.
            // On Strong Beats, penalty is high. On Off-beats, penalty is low.
            let nctPenalty = (100 * conformity) + (50 * metricGravity);

            // Fix: Allow Passing Tones (Stepwise NCTs)
            // If strictly stepwise (dist <= 2) and not on a downbeat (beat 1), reduce penalty.
            if (dist <= 2 && beatStrength < 0.9) {
                nctPenalty *= 0.3; // Slash penalty to encourage flow
            }

            // Panic Mode OR Lyrical Mode: Forgive NCTs to ensure stepwise motion
            // If profile demands steps (>0.7), remove penalty for passing tones
            if ((stepBonus >= 200 || strictMode || intervalProfile.step > 0.7 || melodicRules.forceSteps) && dist <= 2) {
                nctPenalty = 0;
            }

            weight -= nctPenalty;

            // Panic Mode: Force VETO on Leaps (redundancy check)
            if (stepBonus >= 200 && dist > 2) {
                weight -= 1000;
            }

            // 2. Strict Veto (Hard Floor)
            // If conformity is extreme (> 0.8) and note is not part of Triad, Veto.
            // Fix: Bypass Veto if we are Forcing Steps (Lyrical/Epic) to allow passing tones
            if (conformity > 0.8 && metricGravity > 0.5 && stepBonus < 200) {
                 const root = (chord.rootPitchClass % 12);
                 const third = (chord.notes[1] % 12);
                 const fifth = (chord.notes[2] % 12);
                 if (pc !== root && pc !== third && pc !== fifth) {
                     continue;
                 }
            }

            // Penalty for non-chord tones on strong beats unless appoggiatura
            // This is redundant with nctPenalty above, but specific logic for appoggiatura logic check
            if (isStrongBeat && !isAppoggiatura) {
                weight -= 20;
            }
        }

        // Context 2: Interval Physics (Stepwise motion preferred)
        // (dist is already calculated above for chord bonus, using it here)

        // Task 1: The "Jailbreak" (Connect the Sliders)
        // Unlock max interval based on harmonic freedom
        // 0% Freedom -> 7 semitones (Safe)
        // 100% Freedom -> 19 semitones (Wild)
        let maxInterval = 7 + (harmonicFreedom * 12);

    // Data-Driven Override
    if (melodicRules.maxInterval !== undefined) {
        maxInterval = melodicRules.maxInterval;
        }

        // Panic Mode Constraint
        if (stepBonus >= 200) {
            maxInterval = 2; // Strict limit to Major 2nd (Stepwise only)
        }

        // Elastic Tether Logic (Target Authority)
        let isElasticLeap = false;
        if (goalNote !== undefined) {
             // Check if this note gets us significantly closer to goal or IS the goal
             const distToGoal = Math.abs(i - goalNote);
             const prevDistToGoal = Math.abs(center - goalNote);

             // If we are leaping towards the goal (up to 1 octave allowed)
             if (dist > maxInterval && dist <= 12) {
                 if (distToGoal < prevDistToGoal) {
                     isElasticLeap = true;
                     // Bonus for hitting high priority target
                     weight += 50;
                 }
             }
        }

        // Recovery Logic (Gap Fill Enforcer)
        if (context.entropyState && context.entropyState.forceRecovery) {
            // Must step (<= 2)
            if (dist > 2) continue;

            // Must reverse direction
            if (context.prevInterval) {
                 const prevDir = Math.sign(context.prevInterval);
                 const currDir = Math.sign(i - center);
                 if (currDir === prevDir && currDir !== 0) continue;
            }

            // Boost weight to ensure selection
            weight += 100;
        }

        if (dist > maxInterval && !isElasticLeap) {
            continue; // Hard limit based on user intent
        }

        // Task 2: Dynamic Gravity (Scale Leap Penalties)
        // High Freedom reduces cost of leaps.
        let leapCostScalar = 1 + (4 * conformity);

        // Fix: High Complexity Discount
        if (harmonicFreedom > 0.6) {
            leapCostScalar *= 0.6;
        }

        let effectiveLeapPenalty = leapCostScalar;
        if (isChordTone) {
            effectiveLeapPenalty = Math.min(1.0, leapCostScalar); // Cheap movement for arpeggios
        }

        // Apply dynamic leap penalty scaling
        if (melodicRules.leapPenalty !== undefined && effectiveLeapPenalty === leapCostScalar) {
             effectiveLeapPenalty *= melodicRules.leapPenalty;
        }

        // Data-Driven Step Enforcement
        if (melodicRules.forceSteps) {
             if (dist > 4) weight -= 500; // Soft Veto on wide leaps
             if (dist <= 2) weight += 100; // Bonus Step
        }

        // Fix: Momentum (Anti-Machine Gun & Anti-Trill)
        if (dist === 0) {
            weight += repeatWeight;
            if (context.prevInterval === 0) {
                weight -= 40;
            }
            // Panic Mode: Anti-Stagnation (Don't repeat if we need steps)
            if (stepBonus > 100) {
                weight -= 100;
            }
        }
        else if (dist <= 2) {
            weight += stepBonus; // Dynamic Step

            // ANTI-TRILL LOGIC:
            // If previous interval was also a step in the opposite direction (e.g. +2 then -2), it's a trill.
            // If strictMode is OFF, penalize this to encourage broader motion.
            if (!strictMode && context.prevInterval && Math.abs(context.prevInterval) <= 2) {
                // Check direction flip
                const prevDir = Math.sign(context.prevInterval);
                const currDir = Math.sign(i - center);
                if (prevDir !== currDir) {
                    // Panic Mode Escalation: If we are forcing steps (Bonus > 100), we MUST NOT oscillate.
                    // Force a Run or a Rest.
                    if (stepBonus > 100) {
                        weight -= 1000; // Nuclear Veto on Trills in Panic Mode
                    } else {
                        weight -= 100; // Stronger penalty (was 50)
                    }
                }
            }
        }
        else if (dist <= 4) weight += 15; // Skip
        else if (dist <= 7) weight += leapBonus;  // Dynamic Leap
        else weight -= (dist * effectiveLeapPenalty); // Scaled Penalty

        // Task 3: The "Soft Guide" (Tessitura Injection)
        // Fix: "The Cage" - Prioritize Tessitura over default Range
        if (context.tessitura) {
            const { low, high, preferred_center } = context.tessitura;
            // If tessitura is defined, treat it as the primary valid range
            if (low !== undefined && high !== undefined) {
                if (i >= low && i <= high) {
                    weight += 50; // Strong bonus for staying in physical range
                } else {
                    // Reduce penalty if Melodic Range is high (Virtual Virtuosity)
                    const rangePenalty = (melodicRange > 0.7) ? 20 : 100;
                    weight -= rangePenalty; // Strong penalty for going outside (unless forced by goal)
                }
            }
            // Gravitate towards preferred center if defined
            if (preferred_center !== undefined) {
                const distFromCenter = Math.abs(i - preferred_center);
                if (distFromCenter < 12) weight += 20; // Expanded sweet spot
            }
        }

        // Context 3: Recovery (Gap Fill)
        if (context.prevInterval && Math.abs(context.prevInterval) > 4) {
            const isRecovery = (context.prevInterval > 0 && i < center) || (context.prevInterval < 0 && i > center);
            if (isRecovery) weight += 40; // High incentive to return
        }

        // Context 4: Tension (Dissonance)
        // High Tension -> Boost non-chord tones or wide intervals
        if (tension > 0.7) {
            if (!isChordTone && isScaleTone) weight += 15; // Dissonance
            if (dist > 4) weight += 10; // Leaps
        }

        // Context 5: Guide Tones (Voice Leading)
        if (beatStrength > 0.6 && guideTones.includes(pc)) {
            weight += 20;
        }

        // Context 6: Contour (The Editor - Shape Enforcer)
        // Task 2: Melodic Contour Filter
        if (context.contour) {
            const isUp = i > center;
            const isDown = i < center;

            if (context.contour === 'ascending') {
                if (isUp) weight += 30;
                if (isDown) weight -= 100; // Strong Veto
            }
            else if (context.contour === 'descending') {
                if (isDown) weight += 30;
                if (isUp) weight -= 100;
            }
            else if (context.contour === 'arch') {
                // Arch: Up first half, Down second half
                if (progress < 0.5) {
                    if (isUp) weight += 30;
                    if (isDown) weight -= 100;
                } else {
                    if (isDown) weight += 30;
                    if (isUp) weight -= 100;
                }
            }
            // Fallback/Legacy for 'wave' or others
            else if (context.contour === 'wave') {
                 // Alternating UP/DOWN handled by `generateFantasyArpeggio` or similar usually.
                 // For walker, we can stick to simple logic or use beatPos.
                 if (i > center && (context.beatPos % 2 < 1)) weight += 20;
                 else if (i < center) weight += 20;
            }
        }

        // Context 7: Goal Gravity (Task 1: Motivation Engine)
        if (goalNote) {
            const currentDist = Math.abs(i - goalNote);
            const prevDist = Math.abs(center - goalNote);

            // If moving closer to goal, boost
            if (currentDist < prevDist) {
                // Gravity strength increases exponentially with progress
                // Early phrase: Weak pull (Explore)
                // Late phrase: Strong pull (Resolve)
                const pull = Math.pow(progress, 2); // 0.0 -> 0.0, 0.5 -> 0.25, 0.9 -> 0.81
                const gravity = 20 + (pull * 100);
                weight += gravity;
            } else {
                // Moving away penalty
                // Punish moving away heavily in the second half
                const penalty = (progress > 0.5) ? 50 : 10;
                weight -= penalty;
            }

            // High bonus for hitting goal at end of phrase
            if (isLastNote && i === goalNote) weight += 200; // Nuclear magnet
        }

        // Scale adherence (Base requirement)
        if (isScaleTone) weight += 10;
        else weight -= 30;

    // Wiring Up Ghost Variable: weakBeatExtensionWeight
    if (!isStrongBeat && !isChordTone) {
        const extWeight = harmonicProfile.weakBeatExtensionWeight || 0.0;
        if (extWeight > 0) weight += (extWeight * 15);
    }

        // --- NEW: Voice Registry Check (Harmonic & Frequency) ---
        if (context.voiceRegistry) {
            // 1. Direct Frequency Clash (Masking)
            const freqPenalty = checkClash(context.voiceRegistry, currentAbsoluteTime, i, 'melody');
            if (freqPenalty > 0) weight -= freqPenalty;

            // 2. Bar-Wide Harmonic Buffer (Elastic Anchor)
            // Penalize dissonance against ANY sustained note in this bar context
            if (context.voiceRegistry.getBarHarmonicBuffer) {
                const harmonicBuffer = context.voiceRegistry.getBarHarmonicBuffer(context.barIndex || 0);

                // Check candidates against buffer
                // Minor 2nd (1) or Minor 9th (13) against any buffer note?
                const hasDissonance = harmonicBuffer.some(other => {
                    const diff = Math.abs(other - i);
                    return diff === 1 || diff === 13;
                });

                if (hasDissonance) {
                    // "Jazz Context" Exception: If harmonicFreedom is high, allow it?
                    if (harmonicFreedom < 0.7) {
                        weight -= 50; // Heavy penalty for clash in safe mode
                    }
                }
            }
        }

        if (weight > 0) {
            candidates.push({ value: i, weight });
        }
    }

    // Task 1: The "Safety Net" (Fallback Logic)
    // If Veto/Filtering removed all candidates (or constraints were too strict),
    // do NOT return null or center. Apply specific clamping logic.
    if (candidates.length === 0) {
        // "Call getNearestChordTone(previousNote) with a strict limit of 5 semitones."
        // We simulate this by searching chord tones in range.
        const chordTones = chord.notes ? chord.notes.map(n => n % 12) : [chord.rootPitchClass % 12];
        let bestFallback = null;
        let minFallbackDist = Infinity;

        // Search +/- 5 semitones from center (Restricted to 2 if Strict Mode)
        const limit = strictMode ? 2 : 5;
        for (let offset = -limit; offset <= limit; offset++) {
            const pitch = center + offset;
            const pc = pitch % 12;
            // Simplified Fallback Logic
            let isValid = false;
            if (chordTones.includes(pc)) isValid = true;
            else if (strictMode && scalePCs.has(pc) && Math.abs(offset) <= 2) isValid = true; // Allow Scalar Step

            if (isValid) {
                const d = Math.abs(offset);
                if (d < minFallbackDist) {
                    minFallbackDist = d;
                    bestFallback = pitch;
                }
            }
        }

        if (bestFallback !== null) return bestFallback;

        // Ultimate Fallback: Root of the current chord (nearest octave)
        const rootPC = chord.rootPitchClass % 12;
        const distUp = (rootPC - (center % 12) + 12) % 12;
        const distDown = ((center % 12) - rootPC + 12) % 12;
        let fallback = (distUp < distDown) ? center + distUp : center - distDown;

        // Anti-Stagnation: If fallback is same as previous, shift it
        if (fallback === center && candidates.length === 0) {
             fallback += (Math.random() > 0.5 ? 2 : -2); // Force step
        }
        return fallback;
    }

    return weightedRandom(candidates, random);
};

// --- STRATEGIES ---

/**
 * Helper: Apply Breath Logic
 * Forces a rest at the end of a phrase.
 */
const applyBreath = (events, context) => {
    const { barIndex } = context;
    const phraseLength = context.phraseLength || 4;
    // Defect Fix: Breathing Enforcer (Negative Space)
    // Every phrase must end with a Rest equal to at least 25% of phrase length.
    // For 4 bars, that's 1 bar.
    const isPhraseEnd = (barIndex + 1) % phraseLength === 0;

    if (isPhraseEnd) {
        // User Directive: "One beat at most" for silence.
        // Relax breath constraint. Only silence the last beat (Beat 4) to allow for a pickup/breath.
        return events.filter(e => {
            const relStart = e.s - context.barStart;
            // Allow events that start before beat 3.5
            // Or allow everything except what starts in the very last beat?
            // Let's silence Beat 4.
            return relStart < 3.0;
        });
    }
    return events;
};

/**
 * Aligns melody rhythmic accents to the groove (Kick/Snare).
 * @param {Array} events - Melody events
 * @param {Object} grooveContext - { kickPattern, snarePattern, barStart }
 */
export const applyGrooveLocking = (events, grooveContext) => {
    if (!events || events.length === 0 || !grooveContext) return events;

    const { kickPattern, barStart } = grooveContext;
    if (!kickPattern) return events;

    return events.map(ev => {
        const relTime = ev.s - barStart;
        const step = Math.round(relTime * 4); // 16th note grid

        // Check grid alignment
        const gridDiff = Math.abs(relTime - (step * 0.25));

        // Only process notes that are roughly on grid
        if (gridDiff < 0.1) {
            // Check if current step has kick
            const onKick = kickPattern[step % 16];
            if (onKick) return { ...ev, locked: true, vel: Math.min(1.0, (ev.vel || 0.7) + 0.1) }; // Boost velocity on lock

            // Check adjacent steps for kick (syncopation magnet)
            // Look ahead 1 step (anticipation/delay)
            // If melody is on 1.0, Kick is on 1.25 -> Shift to 1.25? (Syncopated)

            const nextStep = (step + 1) % 16;

            // If current is empty of kick, but next has kick, pull towards it?
            // Actually, usually we want to lock accents.
            if (!onKick && kickPattern[nextStep]) {
                // Shift forward to lock
                return { ...ev, s: ev.s + 0.25, locked: true, vel: Math.min(1.0, (ev.vel || 0.7) + 0.15) };
            }
        }
        return ev;
    });
};

/**
 * Helper: Calculate Goal Note (Task 2: Target Upgrade)
 * Step 1: Pick a Goal Note (Chord Tone of the next bar)
 */
const calculateGoalNote = (chord, nextChord, prevNote, context) => {
    // Determine Target
    // If we have a next chord, aim for its chord tones (Voice Leading)
    // If not, aim for current chord's Root (Resolution)
    const targetChord = nextChord || chord;

    // Pick a stable tone (Root, 3rd, 5th) of the target chord
    // Preference: 3rd (Color) > Root (Stability) > 5th (Open)
    const targetRoot = targetChord.rootPitchClass % 12;
    let targetDegree = 0; // Default Root

    // Seeded Random Choice
    const r = context.random ? context.random.next() : Math.random();

    // 50% chance for 3rd (Strong Definition)
    if (r < 0.5) {
        targetDegree = (targetChord.quality === 'minor') ? 3 : 4;
    }
    // 30% chance for Root (Resolution)
    else if (r < 0.8) {
        targetDegree = 0;
    }
    // 20% chance for 5th or 7th (Jazz/Fantasy)
    else {
        targetDegree = 7;
        // If Jazz/Complex, maybe 7th?
        if (context.config && context.config.vectors && context.config.vectors.harmonicComplexity > 0.6) {
            targetDegree = (targetChord.type && targetChord.type.includes('7')) ? 10 : 7;
        }
    }

    const targetPC = (targetRoot + targetDegree) % 12;

    // Find nearest octave to current context (prevNote or center)
    const center = prevNote || 72;
    let bestNote = center;
    let minDist = Infinity;

    // Search octaves 3 to 7
    for (let o = 3; o <= 7; o++) {
        const candidate = targetPC + (o * 12);
        // Constrain to instrument range
        if (context.range && (candidate < context.range.min || candidate > context.range.max)) continue;

        let dist = Math.abs(candidate - center);

        // Dynamic Range: If Melodic Range is High, favor distant octaves
        const melodicRange = (context.config && context.config.vectors) ? context.config.vectors.melodicRange : 0.5;
        if (melodicRange > 0.7) {
            // Invert distance logic: Penalize closeness
            // We want to maximize distance (up to a limit, e.g. 1.5 octaves)
            // Trick: Subtract distance from score?
            // Let's bias the distance calculation.
            // If candidate is > 1 octave away, reduce effective distance.
            if (dist > 12) dist *= 0.5;
        }

        if (dist < minDist) {
            minDist = dist;
            bestNote = candidate;
        }
    }
    return bestNote;
};


export const generateLyricalLine = (chord, rhythmPattern, prevNote, context) => {
    // Task 3: Motif Glue (Phrase Memory)
    // moved logic to Cohesion Check section below to ensure it runs after fallback generation

    // Task 1: Calculate Goal Note
    const goalNote = calculateGoalNote(chord, context.nextChord, prevNote, context);

    // Task 2: Elastic Motif (Duration Awareness)
    const sectionLen = context.sectionLength || 4;
    const durationMode = calculatePhraseStructure(sectionLen);

    // Task 2: The "Global Vault" (Song Memory)
    // Save motifs in Verse/Intro, Recall in Outro
    const sectionType = context.section ? context.section.toUpperCase() : 'UNKNOWN';
    const isPrimary = sectionType.includes('VERSE') || sectionType.includes('INTRO');
    const isRecall = sectionType.includes('OUTRO') || sectionType.includes('REPRISE');

    if (context.songMemory) {
        if (isPrimary && !context.songMemory.motifs['primary']) {
            // Write Mode: Save current rhythm pattern
            context.songMemory.motifs['primary'] = {
                rhythm: rhythmPattern,
                contour: context.contour || 'arch'
            };
        } else if (isRecall && context.songMemory.motifs['primary']) {
            // Read Mode: Recall and Vary
            const seed = context.songMemory.motifs['primary'];
            // Apply Variation: Augmentation (Stretch)
            rhythmPattern = augmentRhythm(seed.rhythm, 1.5); // Stretch by 1.5x
            // Force contour to match seed or vary slightly
            if (!context.contour) context.contour = seed.contour;
        }
    }

    // Override phraseStructure based on Duration if not strictly set
    let phraseStructure = context.phraseStructure || (context.config && context.config.orchestration && context.config.orchestration.phraseStructure);

    if (durationMode === 'compressed') {
        phraseStructure = 'sentence'; // Short, concise
    } else if (durationMode === 'extended' && context.barIndex >= 8) {
        // Second half of long section -> Variation (B Theme)
        phraseStructure = 'period';
    }

    // --- ANACRUSIS (Pickup) GENERATION (Round 2 & 4) ---
    // Round 4: Target the NEXT chord for resolution.
    let leadInEvents = [];
    if (context.generateLeadIn) {
        const beatsPerBar = (context.timeSig ? context.timeSig[0] : 4);
        const pickupStart = context.barStart + beatsPerBar - 1.0; // Last beat

        // Resolve to Next Chord Root (Round 4 Improvement)
        let targetPitch = (chord.rootPitchClass + 7) % 12 + 60; // Default: Current 5th
        if (context.nextChord) {
            // Aim for the Root of the next chord to create a strong cadence
            // Or the 3rd if we want a smoother entry
            targetPitch = context.nextChord.rootPitchClass % 12 + 60;
        }

        // Approach Logic: Stepwise from below or above?
        // Let's do: Target-2 -> Target-1 -> Target (on downbeat)
        // Or Dominant prep: 5th of Target -> Leading Tone of Target

        // Simple Scalar Approach
        // Start 2 steps below target
        const note1 = targetPitch - 2; // Approx
        const note2 = targetPitch - 1; // Leading toneish?

        leadInEvents.push({
            s: pickupStart,
            d: note1,
            len: 0.5,
            vel: 0.6, // Soft
            role: 'melody',
            articulation: 'legato'
        });

        leadInEvents.push({
            s: pickupStart + 0.5,
            d: note2,
            len: 0.5,
            vel: 0.75, // Crescendo
            role: 'melody',
            articulation: 'legato'
        });
    }

    if (phraseStructure && (phraseStructure === 'sentence' || phraseStructure === 'period')) {
        const syntaxEngine = new MelodicSyntax(context.random);

        // Fix: "Flatline" Bug. Initialize motif with contour, not just 0s.
        let motif = [];
        let currentDegree = 0;
        let contourDir = (context.random ? context.random.bool() : Math.random() > 0.5) ? 1 : -1;

        // Inject Complexity into Motif Generation
        const complexity = (context.config && context.config.vectors) ? context.config.vectors.harmonicComplexity : 0.5;
        // Base stay probability: 0.6 (Low Comp) -> 0.2 (High Comp)
        const stayThreshold = 0.6 - (complexity * 0.4);

        rhythmPattern.forEach((r, i) => {
            motif.push({ ...r, scaleDegree: currentDegree });
            // Simple random walk for initial motif
            if (i < rhythmPattern.length - 1) {
                 const move = (context.random ? context.random.next() : Math.random());

                 // Fix: Logic Ordering (Check Skip before Step) & Unreachable Code
                 if (move > (stayThreshold + 0.3)) {
                     // Skip (Large Interval)
                     currentDegree += (contourDir * 2);
                 } else if (move > stayThreshold) {
                     // Step (Small Interval)
                     currentDegree += contourDir;
                 }
                 // Else: Stay (Repetition)

                 // Force direction flip if boundary reached or just randomly
                 if ((context.random ? context.random.bool(0.3) : Math.random() < 0.3)) {
                     contourDir *= -1;
                 }
            }
        });

        // If we have a motif manager, use its motif
        if (context.motifManager && chord.section) {
            const stored = context.motifManager.getStoredMotif(chord.section);
            if (stored) motif = stored;
        }

        // Fix: Inject chord into context for Syntax Engine
        const syntaxContext = { ...context, chord };

        let structuredEvents = [];
        if (phraseStructure === 'sentence') {
            structuredEvents = syntaxEngine.generateSentence(syntaxContext, motif);
        } else {
            structuredEvents = syntaxEngine.generatePeriod(syntaxContext, motif);
        }

        // Apply Apex Logic
        structuredEvents = syntaxEngine.applyApexLogic(structuredEvents);

        // Ensure events have correct absolute time
        const processed = structuredEvents.map(e => ({
            ...e,
            s: context.barStart + (e.s % 4), // Fix time if generate logic returned relative
            role: 'melody'
        }));
        return [...processed, ...leadInEvents];
    }

    // PHASE 3: Motif Integration
    if (context.motifManager && chord.section) {
        const seed = context.motifManager.getMotifForSection(chord.section);
        if (seed && seed.length > 0) {
            // Determine Variation based on phrase position & Tension (State-Aware)
            let variation = 'identity';
            const phrasePos = (context.barIndex || 0) % 4;
            const tension = context.getTension ? context.getTension(context.barIndex || 0) : 0.5;

            if (tension < 0.4) {
                // Low Tension: Subtle / Identity
                if (phrasePos === 3) variation = 'thematic_transformation'; // Slight variation at end
            }
            else if (tension < 0.7) {
                // Med Tension: Structural
                if (phrasePos === 1) variation = 'inversion';
                else if (phrasePos === 2) variation = 'thematic_transformation';
                else if (phrasePos === 3) variation = 'retrograde';
            }
            else {
                // High Tension: Destructive / Intense
                if (phrasePos % 2 === 1) variation = 'diminution'; // Double speed
                else variation = 'agitated'; // Fragment/Repeat
            }

            const variedMotif = context.motifManager.getVariation(variation, chord.section, seed, context);
            let motifEvents = context.motifManager.adaptMotifToHarmony(variedMotif, chord);

            // Map to absolute time
            motifEvents = motifEvents.map(e => ({
                ...e,
                s: context.barStart + e.s,
                role: 'melody'
            }));

            // Apply Breath Logic (Phase 3)
            return applyBreath(motifEvents, context);
        }
    }

    // Fallback: Procedural Walker
    if (!Array.isArray(rhythmPattern) || rhythmPattern.length === 0) {
        // Motif Fragmentation Logic (Filler)
        if (context.motifManager && context.section) {
            // Try to get a rhythm fragment from the motif manager
            // Note: getFragment returns MIDI events with 's' and 'len'. We need to extract just that.
            const motifEvents = context.motifManager.getStoredMotif(context.section);
            const fragment = context.motifManager.getFragment('rhythm_only', motifEvents);

            if (fragment && fragment.length > 0) {
                // Map to pure Rhythm objects {s, l}
                // Normalize start time? getFragment returns relative to its own start.
                // We assume fragment starts at 0.0.
                rhythmPattern = fragment.map(e => ({ s: e.s, l: e.len }));
            }
        }

        // If still no rhythm (Motif failed or unavailable), use procedural fallback
        if (!rhythmPattern || rhythmPattern.length === 0) {
             // Task 2: Entropy Injection (Strategy Pool)
             // Weighted selection of generator type to prevent Euclidean fatigue.
             const strategyRoll = context.random ? context.random.next() : Math.random();
             let useEuclidean = true;

             // 40% Euclidean, 30% Stochastic, 30% Library (if implemented, else Stochastic)
             if (strategyRoll > 0.6) useEuclidean = false; // "Stochastic" mode (simulated via high mutation on simple grid)

             const ts = context.timeSig || [4, 4];
             const num = ts[0];
             const steps = num * 4; // 16ths resolution
             const barStart = 0; // Relative start

             // Dynamic Pulse Count based on Energy/Intensity AND Rhythmic Density
             const intensity = context.intensity || 0.5;

             // Extract Rhythmic Density from config (default 0.5)
             let density = (context.config && context.config.vectors) ? context.config.vectors.melodicDensity : 0.5;

             // --- TASK 2: TEMPO EASE-IN (Offset Density) ---
             // Shift "Active" threshold so 90 BPM is Low Density.
             // Boost only kicks in after 100 BPM.
             const currentBpm = context.bpm || 120;
             const tempoBoost = Math.max(0, (currentBpm - 100) * 0.01); // 0 at 100, 0.2 at 120

             // If BPM < 100, reduce density (Ease-In)
             if (currentBpm < 100) {
                 density *= 0.5; // Reduce by 50% for "Walking Pace" (Breath)
             }

             // Apply Boost
             density += tempoBoost;

             // Task 1: Safe Zone Governor (Rhythm Guard)
             // If RhythmicDensity < 0.3, VETO 16th notes.
             const constraints = getGlobalConstraints(context);
             // Fix: "Density Trap" - Allow context (Tension) to override the constraint.
             // If Tension > 0.5 (Build/Climax), ignore simple rhythm enforcement to allow acceleration.
             const tensionVal = context.getTension ? context.getTension(context.barIndex) : 0.5;
             if (constraints.forceSimpleRhythm && tensionVal < 0.5) {
                 // Force density to result in 8th/Quarter notes.
                 // Reduce pulses significantly
                 density = Math.min(density, 0.2);
             }

             // Task 1: "Legato Transformation" (Lower the Floor)
             // Fix: "Density Slider Fix" - Scale base pulses with density.
             // If density is 0, basePulses -> 1. If density is 1, basePulses -> 4.
             // This allows the slider to actually create sparse textures.
             const basePulses = Math.max(1, Math.ceil(steps * 0.25 * density));

             // Fix: Decoupled Density (User Intent Priority)
             // If density > 0.8, allow it to drive pulse count almost exclusively, regardless of intensity.
             let densityFactor = density;
             if (density > 0.7) {
                 densityFactor = density * 1.5; // Super-charge high density
             } else if (density < 0.3) {
                 densityFactor = density * 0.5; // Suppress low density
             }

             const addedPulses = Math.floor(steps * 0.6 * densityFactor * intensity);

             // Fix: Absolute Pulse Floor (Prevent Silence unless desired)
             // If density < 0.1, allow silence (pulses = 0 is impossible due to Math.max(1)).
             // But we want to allow very sparse (1 note).
             const pulses = Math.max(1, Math.min(steps, basePulses + addedPulses));

             // Phase Shift Logic: Procedural Phrasing (Round 2 Improvement)
             const seed = (context.config && context.config.seed) ? context.config.seed : 'default';
             const sectionType = context.section || 'default';

             // 1. Determine Phrase Type (e.g., AAAB)
             const phraseType = getProceduralPhraseType(seed, sectionType);
             const phrasePattern = getPhrasePattern(phraseType, 4); // 4-bar phrase assumed

             // 2. Determine Rotation Index from Pattern
             const phrasePos = (context.barIndex || 0) % 4;
             const rotationIndex = phrasePattern[phrasePos]; // 0, 1, 2, or 3

             // 3. Calculate Rotate (Base + Variation)
             // Base rotation ensures 'A' isn't always 0 (varies by song)
             const songHash = (seed.toString().charCodeAt(0) || 0);
             const baseRotate = songHash % steps;
             const rotate = baseRotate + (rotationIndex * 4); // Shift 4 steps (1 beat) per variation index

             const binaryPattern = generateEuclideanRhythm(steps, pulses, rotate);

             // Apply Micro-Mutation if Stochastic mode
             const mutationRate = useEuclidean ? 0.0 : 0.2; // 20% variance for non-Euclidean feel
             rhythmPattern = patternToEvents(binaryPattern, barStart, 0.25, 0.9, {
                 microMutation: mutationRate,
                 random: context.random
             });
        }
    }

    // --- TASK 3: THE EDITOR (Cohesion Check) ---
    // Enforce A-A-B-A structural repetition logic
    if (context.cache) {
        const prevPattern = context.cache.lastBarPattern;
        const tension = (context.getTension ? context.getTension(context.barIndex) : 0.5);

        // 1. Probabilistic Glue (Soft Cohesion) - Pre-check
        // Low Tension: 80% chance to Repeat/Vary. High Tension (>0.7): 40% chance.
        const repeatChance = tension < 0.7 ? 0.8 : 0.4;
        const shouldRepeat = prevPattern && (context.random ? context.random.next() : Math.random()) < repeatChance;

        if (shouldRepeat) {
             // 30% chance to Vary (Mutation)
             if ((context.random ? context.random.next() : Math.random()) < 0.3) {
                 rhythmPattern = transformPattern(prevPattern, 'densify');
             } else {
                 rhythmPattern = prevPattern;
             }
        }

        // 2. Strict Veto (Hard Cohesion)
        // If we generated a NEW pattern (didn't repeat above), validate it.
        // If Similarity < 70% and Tension < 0.5 -> VETO and Force Repeat
        if (prevPattern && rhythmPattern) {
            const similarity = calculateSimilarity(rhythmPattern, prevPattern);
            if (tension < 0.5 && similarity < 0.7) {
                // VETO: The generated pattern is too random for this low-tension section.
                // Force Mutation of previous bar.
                rhythmPattern = transformPattern(prevPattern, 'densify'); // "Mutate 1-2 notes"
            }
        }

        // Store for next bar (Final Decision)
        context.cache.lastBarPattern = rhythmPattern;
    }

    // --- ENSEMBLE AWARE MERGING (Defect 1) ---
    // Run consolidation pass before generating pitches
    if (rhythmPattern && rhythmPattern.length > 0) {
        // Mock context extraction (Drum/Bass info needs to be passed in 'context')
        // We assume 'context' has 'ensembleStats' or similar injected from Pipeline.
        // For now, check direct props if available, or fallback to config density.
        const mergeContext = {
            drumDensity: context.drumDensity || (context.config?.vectors?.energy || 0.5), // Proxy
            bassDuration: context.bassAvgDuration || 0, // Proxy
            random: context.random
        };
        rhythmPattern = consolidateElements(rhythmPattern, mergeContext);
    }

    const events = [];
    let currentPitch = prevNote || (60 + chord.rootPitchClass % 12);
    const random = context.random;

    // Pass intervalProfile from context if available
    const intervalProfile = context.intervalProfile;

    // PHRASE STRUCTURE LOGIC (High Priority)
    // Derive structure from barIndex if not explicitly provided
    // Standard 4-bar phrase: Question (0,1), Answer (2,3)
    let phraseType = context.phraseType;
    if (!phraseType) {
        const posInPhrase = context.barIndex % 4;
        if (posInPhrase < 2) phraseType = 'question'; // Antecedent
        else phraseType = 'answer'; // Consequent
    }

    // CONTOUR LOGIC
    // Use explicit contour if provided, otherwise derive
    let contour = context.contour;

    if (!contour) {
        // Waltz Logic: Sway
        if (context.timeSig && context.timeSig[0] === 3) {
            // Up-Down-Up wave for sway feel
            contour = 'wave';
        }
        else {
            // Default to Arch: Up for Question, Down for Answer
            if (phraseType === 'question') {
                contour = 'ascending';
            } else {
                contour = 'descending';
            }

            // Random Variation (if no strict DNA)
            const r = random ? random.next() : Math.random();
            if (r > 0.8) contour = 'wave';
        }
    }

    // IMPRESSIONIST SCALE LOGIC
    // Decide on a scale for this phrase (consistent for the whole line)
    // Fix: Derive deterministically from barIndex phase (2-bar phrases) to prevent flip-flopping.
    let impressionistScale = null;
    if (context.isImpressionist) {
        // Use seeded random for the phrase
        // Phrase ID = floor(barIndex / 2)
        const phraseId = Math.floor((context.barIndex || 0) / 2);
        // We need a stable random seed based on phraseId.
        // Re-use context.random state? No, that advances.
        // Hash phraseId?
        const phraseSeed = (phraseId * 9301 + 49297) % 233280;
        const phraseRandom = phraseSeed / 233280.0;

        // Chance to shift to Whole Tone or Pentatonic for this phrase
        if (phraseRandom > MELODY_WEIGHTS.impressionist.scaleSwitchProb) {
            // Split choice
            impressionistScale = (phraseRandom > (MELODY_WEIGHTS.impressionist.scaleSwitchProb + (1 - MELODY_WEIGHTS.impressionist.scaleSwitchProb)/2)) ? 'whole_tone' : 'pentatonic_major';
        } else {
            impressionistScale = context.scaleType; // Stay normal
        }
    }

    // Track previous interval for Gap Fill logic
    let lastInterval = 0;

    // Entropy State Tracking (Use Context if available)
    const entropyState = context.entropyState || { steps: 0, intervals: 0, stepRatio: 1.0 };

    rhythmPattern.forEach((r, idx) => {
        const isLast = idx === rhythmPattern.length - 1;

        // Task 1: Pass Goal Context
        const progress = idx / rhythmPattern.length; // 0.0 to 1.0

        // Task 3: The "Retry" Loop (Generation Robustness)
        // Attempt to find a valid pitch up to 3 times
        let next = null;
        let attempts = 0;
        const MAX_ATTEMPTS = 3;

        while (next === null && attempts < MAX_ATTEMPTS) {
            next = getNextPitch(currentPitch, chord, context.scaleType, {
                range: context.range || { min: 60, max: 88 },
                contour: contour,
                entropyState: entropyState, // Pass Dynamic State
                rootKey: context.rootKey,
                beatPos: r.s,
                isLastNote: isLast,
                phraseType: phraseType,
                intervalProfile: intervalProfile, // Pass DNA
                isRomantic: context.isRomantic,
                isImpressionist: context.isImpressionist,
                impressionistScale: impressionistScale, // Pass consistent scale
                prevInterval: lastInterval, // Pass history
                random: random,
                config: context.config, // Pass config
                voiceRegistry: context.voiceRegistry, // Pass registry
                barStart: context.barStart,
                barIndex: context.barIndex,
                timeSig: context.timeSig,
                // New Context
                goalNote: goalNote,
                progress: progress
            });
            attempts++;
        }

        // Fallback: If algo fails after retries (should be handled by getNextPitch Safety Net, but as a final guard)
        if (next === null || next === undefined) {
             if (context.strictMode && currentPitch) next = currentPitch; // Repeat note to avoid leap
             else next = (60 + (chord.rootPitchClass % 12)); // Middle C Root
        }

        // Dynamic Velocity
        let vel = 0.7;
        if (context.traits && context.traits.dynamicCurve === 'swell') {
             // Simple swell logic if traits available
             vel = 0.6 + (Math.sin(idx * 0.5) * 0.2);
        } else {
             vel = 0.7 + ((random ? random.next() : Math.random()) * 0.2);
        }

        // --- UPDATED: Raw Duration Output ---
        // Removed applyDurationPhysics here to centralize logic in GenerationPipeline.
        // Fix: NaN Duration Bug - Ensure r.l exists, fallback to 0.25 if missing
        let rawDur = (r.l !== undefined && r.l !== null) ? r.l : (r.len || 0.25);
        if (typeof rawDur !== 'number' || isNaN(rawDur)) rawDur = 0.25;

        let duration = rawDur;

        // SUSPENSION LOGIC (Round 2)
        // If last note of phrase, allow extension into next bar?
        if (isLast && context.allowSuspension) {
            // Check tension? High tension notes want to resolve -> Suspension delays resolution.
            // If chord is V7, extend to resolve on beat 2 of next bar.
            if (Math.random() > 0.6) { // 40% chance
                duration += 1.0; // Extend by a quarter note into next bar
                // This note will overlap with next bar generation.
                // cleanOverlaps in Pipeline needs to handle this.
            }
        }

        // Task 1: "Legato Transformation" (Keyswitches)
        // Default articulation to 'legato' for sustains, 'staccato' only if explicitly short
        // This is a provisional hint; DurationPhysics will refine it.
        let articulation = 'legato';
        if (duration < 0.3) articulation = 'spiccato';
        else if (duration < 0.8) articulation = 'portato';

        events.push({
            s: context.barStart + r.s,
            d: next,
            len: duration,
            vel: vel,
            role: 'melody',
            articulation: articulation
        });

        lastInterval = next - currentPitch;

        // Update Entropy State & Elastic Recovery
        const intAbs = Math.abs(lastInterval);

        // Set Recovery Flag if Leap taken (> 7 semitones)
        if (intAbs > 7) {
            entropyState.forceRecovery = true;
        } else if (intAbs <= 2) {
            entropyState.forceRecovery = false; // Reset once step taken
        }

        if (intAbs > 0) {
            entropyState.intervals++;
            if (intAbs <= 2) entropyState.steps++;
            entropyState.stepRatio = entropyState.intervals > 0 ? (entropyState.steps / entropyState.intervals) : 1.0;
        }

        currentPitch = next;
    });

    return [...events, ...leadInEvents];
};


// ... (Decoration) ...
export const decorateMelody = (events, genre, random, style = 'clean') => {
    if (!events) return events;

    let ornamentationStyle = style;
    // Legacy fallback handled by caller or default
    if (ornamentationStyle === 'clean' || ornamentationStyle === 'none') return events;

    const decorated = [];
    events.forEach(ev => {
        const r1 = random ? random.next() : Math.random();
        // Assuming constants are accessible or hardcoded for decoration
        const longNoteThreshold = 1.0;
        const isLong = ev.len >= longNoteThreshold;

        if (ornamentationStyle === 'trills' && isLong && r1 > 0.5) {
            decorated.push({ ...ev, articulation: 'trill', keyswitch: 11 });
        }
        else if (ornamentationStyle === 'turns' && isLong && r1 > 0.6) {
            decorated.push({ ...ev, articulation: 'trill', keyswitch: 11 });
        }
        else if (ornamentationStyle === 'bends' && ev.len > 0.4 && r1 > 0.7) {
            decorated.push({ ...ev, articulation: 'bend' });
        }
        else {
            decorated.push(ev);
        }
    });
    return decorated;
};

/**
 * Dynamic Strategy Generator
 * Creates a melody generator function on the fly based on a strategy definition object.
 */
const createDynamicStrategy = (definition) => {
    return (chord, rhythmPattern, prevNote, context) => {
        // Merge definition into context
        const dynamicContext = { ...context, ...definition };

        // Dispatch to a base type, but with overridden parameters
        const baseType = definition.base || 'lyrical';
        const generator = getMelodyStrategy(baseType);

        return generator(chord, rhythmPattern, prevNote, dynamicContext);
    };
};

// --- NEW: Cyber Arpeggio (Quantized, Wide) ---
export const generateCyberArpeggio = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const notes = chord.notes || [chord.rootPitchClass];
    // Wide range arpeggio (up to 3 octaves)
    const baseOctave = 48; // C3
    const range = 36;

    rhythmPattern.forEach((r, idx) => {
        const noteIdx = idx % notes.length;
        const note = notes[noteIdx] + baseOctave + (Math.floor(idx / notes.length) * 12);

        events.push({
            s: context.barStart + r.s,
            d: note,
            len: 0.1, // Very short
            vel: 0.9, // Hard
            articulation: 'staccato'
        });
    });
    return events;
};

// --- NEW: Chant / Plainsong (Stepwise, Narrow) ---
export const generateChant = (chord, rhythmPattern, prevNote, context) => {
    // Override rhythm to be fluid (quarters/halves)
    // Ignore rhythmPattern if it's too fast?
    // Use intervalProfile: chant
    const chantContext = { ...context, intervalProfile: MELODY_WEIGHTS.intervalProfiles.chant };
    return generateLyricalLine(chord, rhythmPattern, prevNote, chantContext);
};

// --- NEW: Femme Fatale (Noir Lyrical) ---
export const generateFemmeFatale = (chord, rhythmPattern, prevNote, context) => {
    const noirContext = {
        ...context,
        contour: 'descending', // Languid descent
        scaleType: 'harmonic_minor' // Force harmonic minor feel if possible
    };
    return generateLyricalLine(chord, rhythmPattern, prevNote, noirContext);
};

// --- NEW: Carol Melody (Simple, Singing) ---
export const generateCarolMelody = (chord, rhythmPattern, prevNote, context) => {
    // Simple rhythms, mostly chord tones
    // Boost chord tone weight?
    // Handled by default profile mostly, but ensure 'simple' contour
    const carolContext = { ...context, contour: 'arch' };
    return generateLyricalLine(chord, rhythmPattern, prevNote, carolContext);
};

// --- NEW: Copland-esque (Wide Intervals) ---
export const generateCoplandEsque = (chord, rhythmPattern, prevNote, context) => {
    const coplandContext = { ...context, intervalProfile: MELODY_WEIGHTS.intervalProfiles.wide };
    return generateLyricalLine(chord, rhythmPattern, prevNote, coplandContext);
};

// --- NEW: Virtuoso Solo (High Energy / Chromatic) ---
export const generateVirtuosoSolo = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const random = context.random;
    let currentPitch = prevNote || (60 + chord.rootPitchClass % 12);

    // Override rhythm for virtuosity (mostly 16ths, occasional 32nd runs)
    // Ignore rhythmPattern mostly, or use it as anchor points
    const notesPerBeat = 4; // 16ths
    const totalNotes = 16;

    // Strategy: Target chord tones on strong beats, use chromatic passing tones between
    const chordNotes = chord.notes ? chord.notes.map(n => n%12) : [chord.rootPitchClass % 12];

    for(let i=0; i<totalNotes; i++) {
        const time = context.barStart + (i * 0.25);
        const beatPos = i % 4;

        let nextPitch = currentPitch;

        // On beat (0, 4, 8, 12): Target a chord tone
        if (i % 4 === 0) {
            // Find nearest chord tone? Or leap to one?
            // Leap allowed
            const candidates = [];
            for(let oct=4; oct<=6; oct++) {
                chordNotes.forEach(pc => candidates.push(pc + (oct*12)));
            }
            // Pick one near currentPitch but maybe higher for excitement
            // Filter candidates in range
            const valid = candidates.filter(n => Math.abs(n - currentPitch) < 15);
            if (valid.length > 0) {
                nextPitch = valid[Math.floor((random ? random.next() : Math.random()) * valid.length)];
            }
        } else {
            // Off beat: Stepwise (scale or chromatic)
            // 30% chance of chromatic passing note
            const isChromatic = (random ? random.next() : Math.random()) > 0.7;
            const direction = (random ? random.next() : Math.random()) > 0.5 ? 1 : -1;

            if (isChromatic) {
                nextPitch = currentPitch + direction;
            } else {
                // Diatonic step
                // Use getNextPitch helper?
                // Just use simple scalar logic:
                // If major scale, step 1 or 2 semitones
                nextPitch = currentPitch + (direction * 2); // Crude approximation, but fast
                // Ideally check scale
            }
        }

        events.push({
            s: time,
            d: nextPitch,
            len: 0.2, // Short
            vel: (i % 4 === 0) ? 0.9 : 0.7, // Accent beats
            articulation: 'legato',
            role: 'solo'
        });
        currentPitch = nextPitch;
    }

    return events;
};

// --- Strategies that MUST be exported for MusicEngine ---

export const generateRiff = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    let currentPitch = prevNote || (64 + chord.rootPitchClass % 12);
    const random = context.random;

    rhythmPattern.forEach(r => {
        const useBlues = context.scaleType.includes('minor') || context.scaleType.includes('blues');
        const effectiveScale = useBlues ? 'blues' : context.scaleType;

        const next = getNextPitch(currentPitch, chord, effectiveScale, {
            range: { min: 55, max: 79 },
            contour: 'wave',
            rootKey: context.rootKey,
            beatPos: r.s,
            random: random,
            config: context.config,
            voiceRegistry: context.voiceRegistry,
            barStart: context.barStart,
            barIndex: context.barIndex
        });

        // Use raw duration, let DurationPhysics handle it
        let rawDur = (r.l !== undefined && r.l !== null) ? r.l : (r.len || 0.25);
        if (typeof rawDur !== 'number' || isNaN(rawDur)) rawDur = 0.25;

        events.push({
            s: context.barStart + r.s,
            d: next,
            len: rawDur,
            vel: 0.85,
            articulation: 'short'
        });
        currentPitch = next;
    });

    return events;
};

export const generateCounterpoint = (chord, rhythmPattern, prevNote, context) => {
    const random = context.random;

    // Counter-Contour Logic
    const leadContour = context.barIndex % 4 < 2 ? 'ascending' : 'descending';
    // 80% chance to be contrary, 20% parallel
    const isContrary = (random ? random.next() : Math.random()) > 0.2;
    const myContour = isContrary ? (leadContour === 'ascending' ? 'descending' : 'ascending') : leadContour;

    const events = [];
    let currentPitch = prevNote || (48 + chord.rootPitchClass % 12);

    rhythmPattern.forEach(r => {
        const next = getNextPitch(currentPitch, chord, context.scaleType, {
            range: { min: 48, max: 72 },
            contour: myContour,
            rootKey: context.rootKey,
            beatPos: r.s,
            random: random,
            config: context.config,
            voiceRegistry: context.voiceRegistry,
            barStart: context.barStart,
            barIndex: context.barIndex
        });

        // Use raw duration
        let rawDur = (r.l !== undefined && r.l !== null) ? r.l : (r.len || 0.25);
        if (typeof rawDur !== 'number' || isNaN(rawDur)) rawDur = 0.25;

        events.push({
            s: context.barStart + r.s,
            d: next,
            len: rawDur,
            vel: 0.6,
            role: 'counter_melody',
            articulation: 'legato'
        });
        currentPitch = next;
    });

    return events;
};

export const generateArpeggio = (chord, rhythmPattern, prevNote, context) => {
    if (!Array.isArray(rhythmPattern)) return [];
    const events = [];
    const root = chord.rootPitchClass;
    const random = context.random;

    const chordNotes = [];
    for (let oct = 4; oct <= 6; oct++) {
        chord.notes.forEach(n => {
            chordNotes.push((n % 12) + (oct * 12));
        });
    }

    chordNotes.sort((a,b) => a - b);

    // RANDOMIZED DIRECTION
    // Default was strict based on barIndex. Now mix it up.
    // 0 = Up, 1 = Down, 2 = Converge/Diverge?
    const dirRoll = random ? random.next() : Math.random();
    let direction = 1; // Up
    if (dirRoll > 0.6) direction = -1; // Down
    else if (dirRoll > 0.4) direction = (context.barIndex % 2 === 0) ? 1 : -1; // Classic

    // Start index
    let idx = 0;
    if (direction === -1) idx = chordNotes.length - 1;
    // Random start offset?
    if (dirRoll < 0.1) idx = Math.floor((random ? random.next() : Math.random()) * chordNotes.length);

    rhythmPattern.forEach((r, stepIdx) => {
        const note = chordNotes[idx];

        // Use raw duration
        let rawDur = (r.l !== undefined && r.l !== null) ? r.l : (r.len || 0.25);
        if (typeof rawDur !== 'number' || isNaN(rawDur)) rawDur = 0.25;

        events.push({
            s: context.barStart + r.s,
            d: note,
            len: rawDur,
            vel: 0.8
        });

        idx += direction;
        if (idx >= chordNotes.length) idx = 0;
        if (idx < 0) idx = chordNotes.length - 1;
    });

    return events;
};

export const generateMotific = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    let currentPitch = prevNote || (72 + chord.rootPitchClass % 12);
    const random = context.random;

    rhythmPattern.forEach(r => {
        const next = getNextPitch(currentPitch, chord, context.scaleType, {
            range: { min: 60, max: 84 },
            contour: 'static',
            rootKey: context.rootKey,
            beatPos: r.s,
            random: random,
            config: context.config,
            voiceRegistry: context.voiceRegistry,
            barStart: context.barStart,
            barIndex: context.barIndex
        });

        events.push({
            s: context.barStart + r.s,
            d: next,
            len: r.l,
            vel: 0.75
        });
        // Jump only occasionally
        if ((random ? random.next() : Math.random()) > 0.5) currentPitch = next;
    });

    return events;
};

export const generateOstinato = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const root = chord.rootPitchClass % 12;
    const baseOctave = 60;
    const random = context.random;

    // RANDOMIZED CELL
    const variantKey = context.traits?.melodicRules?.ostinatoVariant || 'standard';
    const variants = MELODY_WEIGHTS.ostinato.cells[variantKey] || MELODY_WEIGHTS.ostinato.cells.standard;

    // Construct actual intervals relative to root
    const cellTemplates = variants.map(cell => cell.map(interval => root + interval));

    const cellIdx = Math.floor((random ? random.next() : Math.random()) * cellTemplates.length);
    const selectedCell = cellTemplates[cellIdx].map(n => n + baseOctave);

    let rateStr = context.rate || '16n';

    // Override rate with Rhythmic Density if available
    if (context.config && context.config.vectors) {
        const density = context.config.vectors.melodicDensity;
        if (density < 0.3) rateStr = '8n';
        else if (density > 0.7) rateStr = '16n';
    }

    let stepSize = 0.25;
    let loopCount = 16;

    if (rateStr === '8n') { stepSize = 0.5; loopCount = 8; }
    if (rateStr === '8t') { stepSize = 1/3; loopCount = 12; }

    for (let i = 0; i < loopCount; i++) {
        const note = selectedCell[i % 4];
        const vel = (i % 4 === 0) ? 0.9 : 0.6;

        events.push({
            s: context.barStart + (i * stepSize),
            d: note,
            len: stepSize * 0.8, // Explicit staccato for ostinato
            vel: vel,
            articulation: 'spiccato',
            role: 'ostinato_layer'
        });
    }

    return events;
};

export const generateSwarm = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const random = context.random;
    const count = Math.floor(fuzz(MELODY_WEIGHTS.swarm.count, 4, random)); // Randomize count

    for(let i=0; i<count; i++) {
        const s = context.barStart + ((random ? random.next() : Math.random()) * 4.0);
        const len = 0.1 + ((random ? random.next() : Math.random()) * 0.4);
        const pitch = MELODY_WEIGHTS.swarm.pitchBase + Math.floor((random ? random.next() : Math.random()) * MELODY_WEIGHTS.swarm.pitchRange);

        events.push({
            s: s,
            d: pitch,
            len: len,
            vel: Math.min(1.0, MELODY_WEIGHTS.swarm.velocityBase + ((random ? random.next() : Math.random()) * MELODY_WEIGHTS.swarm.velocityVar)),
            articulation: 'spiccato',
            role: 'swarm'
        });
    }
    return events.sort((a,b) => a.s - b.s);
};

// --- NEW: Serialism (Twelve Tone Row) ---
export const generateSerialism = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const random = context.random;

    // Generate or Retrieve Row (12 tones)
    // For true serialism, we need state persistence.
    // For now, generate a random permutation of 0-11
    let row = [0,1,2,3,4,5,6,7,8,9,10,11];
    for (let i = row.length - 1; i > 0; i--) {
        const j = Math.floor((random ? random.next() : Math.random()) * (i + 1));
        [row[i], row[j]] = [row[j], row[i]];
    }

    // Apply to rhythm
    rhythmPattern.forEach((r, idx) => {
        const pc = row[idx % 12];
        // Octave displacement (Angular melody)
        const octave = 4 + Math.floor((random ? random.next() : Math.random()) * 3); // 4, 5, 6
        const note = pc + (octave * 12);

        events.push({
            s: context.barStart + r.s,
            d: note,
            len: r.l,
            vel: 0.7 + ((random ? random.next() : Math.random()) * 0.3), // Wide dynamic range
            articulation: 'staccato' // Pointillism
        });
    });

    return events;
};

// --- NEW: Impressionist Wander (Whole Tone / Pentatonic Drifting) ---
export const generateImpressionistWander = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    let currentPitch = prevNote || (60 + chord.rootPitchClass % 12);
    const random = context.random;
    const { min = 60, max = 96 } = context.range || {};

    // Fluid rhythm: Tuplets (5s or 7s) or Irregular spacing?
    // Let's generate a stream of ~8 notes per bar but with random offsets
    const count = Math.floor(fuzz(MELODY_WEIGHTS.impressionist.wanderCount, 2, random)); // 5-9 notes
    const stepSize = 4 / count; // fit to bar

    for(let i=0; i<count; i++) {
        // Drift timing slightly
        const time = context.barStart + (i * stepSize) + fuzz(0, MELODY_WEIGHTS.impressionist.driftAmount, random);

        // Use getNextPitch with isImpressionist flag
        const next = getNextPitch(currentPitch, chord, context.scaleType, {
            range: { min, max },
            contour: 'wave',
            rootKey: context.rootKey,
            beatPos: time % 4,
            traits: { melodicRules: MELODIC_PROFILE_PRESETS.impressionist },
            random: random,
            config: context.config,
            voiceRegistry: context.voiceRegistry,
            barStart: context.barStart,
            barIndex: context.barIndex
        });

        events.push({
            s: time,
            d: next,
            len: stepSize * 1.2, // Overlap slightly (Blur)
            vel: fuzz(0.65, 0.15, random), // Soft dynamics
            articulation: 'legato'
        });
        currentPitch = next;
    }
    return events;
};

// --- NEW: Fantasy Arpeggio (Prelude Style) ---
export const generateFantasyArpeggio = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const chordNotes = [];
    const { min = 48, max = 84 } = context.range || {};
    const random = context.random;

    // Calculate available octaves based on instrument range
    // Ensure we have at least one octave of sweep space
    const startOct = Math.floor(min / 12);
    const endOct = Math.floor(max / 12);

    for (let oct = startOct; oct <= endOct; oct++) {
        chord.notes.forEach(n => {
            const note = (n % 12) + (oct * 12);
            if (note >= min && note <= max) {
                chordNotes.push(note);
            }
        });
    }
    chordNotes.sort((a,b) => a - b);

    // If no notes fit (rare), fallback to root in range
    if (chordNotes.length === 0) {
        chordNotes.push(Math.max(min, Math.min(max, (chord.rootPitchClass % 12) + 60)));
    }

    // Fast 16th note sweep (Reduced from 32nd to prevent note spam)
    const stepSize = MELODY_WEIGHTS.fantasy.stepSize;
    const count = MELODY_WEIGHTS.fantasy.arpeggioLoop; // Fill bar

    // RANDOMIZED WAVE SHAPE
    // 0=Up/Down, 1=Down/Up, 2=Up only (reset), 3=Random walk
    const waveType = Math.floor((random ? random.next() : Math.random()) * 4);

    for(let i=0; i<count; i++) {
        const n = chordNotes.length;
        let noteIdx = 0;

        if (waveType === 0) { // Up/Down
            const phase = Math.floor(i / n) % 2;
            noteIdx = (phase === 0) ? (i % n) : ((n - 1) - (i % n));
        } else if (waveType === 1) { // Down/Up
            const phase = Math.floor(i / n) % 2;
            noteIdx = (phase === 0) ? ((n - 1) - (i % n)) : (i % n);
        } else if (waveType === 2) { // Up Only (Sawtooth)
            noteIdx = i % n;
        } else { // Random
            noteIdx = Math.floor((random ? random.next() : Math.random()) * n);
        }

        const note = chordNotes[noteIdx];
        events.push({
            s: context.barStart + (i * stepSize),
            d: note,
            len: stepSize,
            vel: 0.8 + (Math.sin(i*0.2)*0.2), // Boost velocity for High Voice Distribution
            articulation: 'legato'
        });
    }
    return events;
};

// --- NEW: Phasing Pattern (Minimalist / Steve Reich) ---
export const generatePhasingPattern = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const root = chord.rootPitchClass;
    const { phaseShiftBarInterval, phaseShiftAmount } = MELODY_WEIGHTS.minimalist;
    const NOTE_DURATION = 0.4;
    const NOTE_VELOCITY = 0.6;

    // Use rhythmPattern if provided, otherwise default to 8th notes
    let steps = Array.from({ length: 8 }, (_, i) => i * 0.5);
    if (rhythmPattern && rhythmPattern.length > 0) {
        steps = rhythmPattern.map(r => r.s);
    }

    // Fixed melodic cell based on chord
    const cell = [root + 60, root + 64, root + 67, root + 71, root + 72]; // Pentatonic-ish

    // Phase shift: Shift start time by 16th note every X bars
    const phaseShift = Math.floor(context.barIndex / phaseShiftBarInterval) * phaseShiftAmount;

    steps.forEach((step, i) => {
        const time = context.barStart + step + phaseShift;
        const note = cell[i % cell.length];

        events.push({
            s: time,
            d: note,
            len: NOTE_DURATION,
            vel: NOTE_VELOCITY,
            articulation: 'short'
        });
    });
    return events;
};

// --- NEW: Additive Motif (Minimalist / Philip Glass) ---
export const generateAdditiveMotif = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const root = chord.rootPitchClass;
    const { additiveInterval, additiveStep } = MELODY_WEIGHTS.minimalist;

    // Base motif: 3 notes
    const baseMotif = [root + 60, root + 67, root + 64];

    // Add 1 note every X bars
    const addedNotes = Math.floor(context.barIndex / additiveInterval);
    const totalLength = baseMotif.length + addedNotes;

    // Generate extended motif sequence
    const sequence = Array.from({ length: totalLength }, (_, i) => baseMotif[i % baseMotif.length]);

    // Place notes (16ths)
    sequence.forEach((note, idx) => {
        const time = context.barStart + (idx * additiveStep);
        // Only if it fits in the bar (for simplicity)
        if (time < context.barStart + 4.0) {
            events.push({
                s: time,
                d: note,
                len: 0.2,
                vel: 0.7,
                articulation: 'spiccato'
            });
        }
    });

    return events;
};

// --- ENHANCEMENT: Advanced Melodic Sequencing (Sequencer) ---
export const generateSequence = (chord, rhythmPattern, prevNote, context) => {
    const events = [];
    const { barStart, barIndex, scaleType } = context;
    const startPitch = prevNote || (60 + chord.rootPitchClass % 12);
    const random = context.random;

    // Sequence Step logic: Every bar, move up. Reset every 4 bars.
    const sequenceStep = barIndex % 4;

    rhythmPattern.forEach((r) => {
        // Shift "center" of gravity up based on sequence step
        const sequenceCenter = startPitch + (sequenceStep * 2); // Roughly +2 semitones per bar

        const next = getNextPitch(sequenceCenter, chord, scaleType, {
            ...context,
            contour: 'ascending', // Force upward drive in sequence
            random: random,
            config: context.config,
            voiceRegistry: context.voiceRegistry,
            barStart: context.barStart,
            barIndex: context.barIndex
        });

        events.push({
            s: barStart + r.s,
            d: next,
            len: r.l,
            vel: 0.8,
            role: 'melody_seq'
        });
    });

    return events;
};

// --- NEW: Call & Response (Internal Dialogue) ---
export const generateCallAndResponse = (chord, rhythmPattern, prevNote, context) => {
    const baseEvents = generateLyricalLine(chord, rhythmPattern, prevNote, context);
    const random = context.random;

    // Variance 1: Split Point (Beat 2, 3, or 1)
    const splitRoll = random ? random.next() : Math.random();
    let splitPoint = 2.0;
    if (splitRoll > 0.6) splitPoint = 3.0; // Long Call, Short Response
    else if (splitRoll > 0.8) splitPoint = 1.0; // Short Call, Long Response

    // Variance 2: Response Behavior (Shift Down, Shift Up, or Echo)
    const modeRoll = random ? random.next() : Math.random();
    let mode = 'low_response';
    if (modeRoll > 0.6) mode = 'high_response';
    else if (modeRoll > 0.8) mode = 'echo'; // Same pitch, softer

    return baseEvents.map(ev => {
        const beat = (ev.s - context.barStart) % 4;

        // Call (Before Split)
        if (beat < splitPoint) {
            return {
                ...ev,
                vel: Math.min(1.0, (ev.vel || 0.7) + 0.1),
                articulation: 'marcato',
                role: 'call'
            };
        }
        // Response (After Split)
        else {
            let d = ev.d;
            let vel = (ev.vel || 0.7) * 0.8;
            let artic = 'staccato';

            if (mode === 'low_response') {
                d -= 12;
                if (d < 48) d += 12;
            } else if (mode === 'high_response') {
                d += 12;
                if (d > 96) d -= 12;
                artic = 'legato';
            }
            // Echo: Keep pitch, lower velocity

            return {
                ...ev,
                d: d,
                vel: vel,
                articulation: artic,
                role: 'response'
            };
        }
    });
};

export const generateThroughComposed = (chord, rhythmPattern, prevNote, context) => {
    // Continuous line, ignores bar divisions logic
    // Just wrap lyrical line but ensure no breath gaps in context
    const throughContext = { ...context, phraseLength: 16 }; // Very long phrase
    return generateLyricalLine(chord, rhythmPattern, prevNote, throughContext);
};

export const generateSentenceStructure = (chord, rhythmPattern, prevNote, context) => {
    // Sentence: Short (2), Short (2), Long (4)
    // We are generating 1 bar.
    // We need to know where we are in the sentence.
    // Phrase pos: 0, 1 (Presentation), 2, 3 (Continuation/Cadence)

    // This logic is best handled by MelodicSyntax inside generateLyricalLine
    // But we can force parameters here.

    // If Bar 0 or 1: Force "Motific" (Repetitive)
    // If Bar 2 or 3: Force "Flow" (Linear)

    const pos = (context.barIndex || 0) % 4;
    let strategy = 'motific';
    if (pos >= 2) strategy = 'lyrical'; // Flow

    const gen = getMelodyStrategy(strategy);
    return gen(chord, rhythmPattern, prevNote, context);
};

export const getMelodyStrategy = (name) => {
    // Support Custom Strategy Objects
    if (typeof name === 'object' && name !== null) {
        return createDynamicStrategy(name);
    }

    // Phase 5: Swarm Activation
    // The Horror genre maps to 'swarm' string, but just in case, ensure it's handled.
    if (name === 'Horror' || name === 'Horror / Tension') return generateSwarm;

    // Direct string mapping
    const strategyMap = {
        'call_and_response': generateCallAndResponse,
        'lyrical': generateLyricalLine,
        'riff': generateRiff,
        'arpeggio': generateArpeggio,
        'motific': generateMotific,
        'ostinato': generateOstinato,
        'counterpoint': generateCounterpoint,
        'swarm': generateSwarm,
        'fantasy_arpeggio': generateFantasyArpeggio,
        'phasing': generatePhasingPattern,
        'additive': generateAdditiveMotif,
        'impressionist_wander': generateImpressionistWander,
        'sequence': generateSequence,
        'serialism': generateSerialism,
        'twelve_tone': generateSerialism,
        'cyber_arpeggio': generateCyberArpeggio,
        'chant': generateChant,
        'plainsong': generateChant,
        'femme_fatale': generateFemmeFatale,
        'carol_melody': generateCarolMelody,
        'copland_esque': generateCoplandEsque,
        'virtuoso': generateVirtuosoSolo,
        'virtuoso_solo': generateVirtuosoSolo,
        'through_composed': generateThroughComposed,
        'sentence': generateSentenceStructure
    };

    if (strategyMap[name]) return strategyMap[name];

    // Seeded Variant Selection (Dynamic Dispatch)
    // If the strategy name is like "lyrical_variant" (which doesn't exist yet, but we can simulate variety here).
    // Actually, the prompt asks to use seed to pick a variant.
    // Since 'getMelodyStrategy' is usually called once per section orchestration with the strategy name string,
    // we can't easily inject the seed HERE to return a different FUNCTION.
    // Instead, the variety must happen INSIDE the strategy function using context.random.

    // However, if we want STRUCTURAL variety (e.g. Lyrical A vs Lyrical B), we can wrap it.

    if (name === 'lyrical') {
        return (chord, rhythm, prev, context) => {
            // Use seeded random to pick flavor
            // Flavor A: Standard (Arch)
            // Flavor B: Delayed Entry (Start on beat 2)
            // Flavor C: Fragmented (Short bursts)
            const roll = context.random ? context.random.next() : Math.random();
            if (roll > 0.7) {
                // Flavor B: Delayed Entry
                // Modify context or rhythm before calling base
                // We can just filter the result
                const events = generateLyricalLine(chord, rhythm, prev, context);
                return events.filter(e => (e.s - context.barStart) >= 1.0);
            }
            return generateLyricalLine(chord, rhythm, prev, context);
        };
    }

    return generateLyricalLine;
};
